import React, { useState } from 'react'
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../ui/card"
import { UploadIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react'
import { Progress } from "../ui/progress"
import { useImportCustomerCodeMappingsMutation } from '../../store/is/customerCodeMappingApi'

interface CustomerCodeMappingImportProps {
  onSuccess: () => void
  onCancel: () => void
}

const CustomerCodeMappingImport: React.FC<CustomerCodeMappingImportProps> = ({
  onSuccess,
  onCancel
}) => {
  const [file, setFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<{
    total: number
    success: number
    failed: number
    errors?: string[]
  } | null>(null)

  const [importMappings] = useImportCustomerCodeMappingsMutation()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
    }
  }

  const handleImport = async () => {
    if (!file) return
    if (!(file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setResult({ total: 0, success: 0, failed: 1, errors: ['Please select a CSV file for mappings import.'] })
      return
    }

    setIsLoading(true)
    setResult(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await importMappings(formData).unwrap()

      setResult({
        total: (response.successful_imports || 0) + (response.failed_imports || 0),
        success: response.successful_imports || 0,
        failed: response.failed_imports || 0,
        errors: response.errors || []
      })

      if ((response.failed_imports || 0) === 0) {
        setTimeout(() => {
          onSuccess()
        }, 2000)
      }
    } catch (error) {
      console.error('Mappings import failed:', error)
      setResult({
        total: 0,
        success: 0,
        failed: 1,
        errors: [typeof error === 'string' ? error : 'The mappings import operation failed. Please try again.']
      })
    } finally {
      setIsLoading(false)
    }
  }

  const successPercentage = result?.total ? Math.round((result.success / result.total) * 100) : 0

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Import Customer Code Mappings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-2 border-dashed rounded-lg p-4 text-center">
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="mapping-file-upload"
            disabled={isLoading}
          />
          <label 
            htmlFor="mapping-file-upload" 
            className="flex flex-col items-center justify-center h-32 cursor-pointer"
          >
            <UploadIcon className="h-10 w-10 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {file ? file.name : 'Click to upload customer_code_mappings_sample.csv'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Expected headers: org_id,input_code,customer_input_code,(optional) customer_inhibitor_code,customer_crop_code,customer_soil_code
            </p>
          </label>
        </div>

        {result && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-semibold">{result.total}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Success</p>
                <p className="text-2xl font-semibold text-green-600">{result.success}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-semibold text-red-600">{result.failed}</p>
              </div>
            </div>

            <Progress value={successPercentage} className="h-2" />

            {result.errors && result.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm mt-4">
                <div className="flex items-center gap-2 text-red-600 mb-2">
                  <AlertCircleIcon className="h-4 w-4" />
                  <p className="font-medium">Import Errors</p>
                </div>
                <ul className="list-disc pl-5 text-sm space-y-1 max-h-32 overflow-y-auto">
                  {result.errors.map((error, index) => (
                    <li key={index} className="text-red-700">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.failed === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm mt-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircleIcon className="h-4 w-4" />
                  <p className="font-medium">All records imported successfully!</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-end space-x-2">
        <Button 
          variant="outline" 
          onClick={onCancel} 
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleImport} 
          disabled={!file || isLoading}
          className="relative"
        >
          {isLoading ? 'Importing...' : 'Import Mappings'}
          {isLoading && (
            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
          )}
        </Button>
      </CardFooter>
    </Card>
  )
}

export default CustomerCodeMappingImport

